import axios from 'axios';

/**
 * initiate S3 multipart upload
 * @param {File} file - The file to upload
 * @param {string} orderToken - The order UUID token
 * @param {string} imageType - Type of image ('original' or 'sample')
 * @returns {Promise<Object>} Upload session data including upload_id, object_key, cdn_url
 */
export async function initiateMultipartUpload(file, orderToken, imageType = 'original') {
  try {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    const response = await axios.post('/images/multipart/initiate', {
      filename: file.name,
      file_type: fileExtension,
      file_size: file.size,
      token: orderToken,
      image_type: imageType,
    });

    return response.data;
  } catch (error) {
    console.error('Error initiating multipart upload:', error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      'Failed to initiate upload. Please try again.'
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };

    throw enhancedError;
  }
}

/**
 * Get presigned URL for specific part/chunk
 * @param {string} uploadId - The multipart upload ID
 * @param {string} objectKey - The S3 object key
 * @param {number} partNumber - The part number (1-based)
 * @param {string} orderToken - The order UUID token
 * @returns {Promise<Object>} Presigned URL data including url, already_uploaded flag, etag, parts_completed
 */
export async function getPresignedPartUrl(uploadId, objectKey, partNumber, orderToken) {
  try {
    const response = await axios.post('/images/multipart/generate-presigned-url-for-part', {
      upload_id: uploadId,
      object_key: objectKey,
      part_number: partNumber,
      token: orderToken,
    });

    return response.data;
  } catch (error) {
    console.error(`Error getting presigned URL for part ${partNumber}:`, error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      `Failed to get upload URL for part ${partNumber}`
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };

    throw enhancedError;
  }
}

/**
 * Complete multipart upload (assemble chunks)
 * @param {string} uploadId - The multipart upload ID
 * @param {string} objectKey - The S3 object key
 * @param {Array<{part_number: number, etag: string}>} parts - Array of uploaded parts with ETags
 * @param {string} filename - Original filename
 * @param {number} fileSize - File size in bytes
 * @param {string} cdnUrl - CDN URL of the file
 * @param {string} orderToken - The order UUID token
 * @param {string} imageType - Type of image ('original' or 'sample')
 * @returns {Promise<Object>} Image record from backend
 */
export async function completeMultipartUpload(
  uploadId, 
  objectKey, 
  parts, 
  filename, 
  fileSize, 
  cdnUrl, 
  orderToken, 
  imageType
) {
  try {
    const response = await axios.post('/images/multipart/complete', {
      upload_id: uploadId,
      object_key: objectKey,
      token: orderToken,
      parts: parts,
      filename: filename,
      file_size: fileSize,
      cdn_url: cdnUrl,
      image_type: imageType,
    });

    return response.data;
  } catch (error) {
    console.error('Error completing multipart upload:', error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      'Failed to complete upload. Please contact support.'
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };
    
    throw enhancedError;
  }
}

/**
 * Abort multipart upload (cleanup)
 * @param {string} uploadId - The multipart upload ID
 * @param {string} objectKey - The S3 object key
 * @param {string} orderToken - The order UUID token
 * @returns {Promise<void>}
 */
export async function abortMultipartUpload(uploadId, objectKey, orderToken) {
  try {
    await axios.delete('/images/multipart/abort', {
      data: {
        upload_id: uploadId,
        object_key: objectKey,
        token: orderToken,
      },
    });
  } catch (error) {
    console.error('Error aborting multipart upload:', error);

    // no need to throw here coz abort failure is non-critical
  }
}
